import { useCallback, useEffect, useState } from "react";
import axiosInstance from "@/helpers/axiosInstance";

interface RemainingResponse {
  remainingMillis: number;
}

/**
 * 세션 남은 시간(ms) 상태 반환
 */
export function useRemainingSession(): {
  remainingMs: number | null;
  format: (ms: number | null) => string;
  extend: () => Promise<void>;
} {
  const [remainingMs, setRemainingMs] = useState<number | null>(null);

  useEffect(() => {
    let timerId: NodeJS.Timeout | null = null;
    let stopped = false;

    const fetchRemaining = async () => {
      try {
        const res = await axiosInstance.get<RemainingResponse>("/session/remaining");
        setRemainingMs(res.data.remainingMillis);
      } catch (err: any) {
        if (err?.response?.status === 401) {
          stopped = true;
          setRemainingMs(0);
          if (timerId) clearInterval(timerId);
        } else {
          setRemainingMs(0);
        }
      }
    };

    fetchRemaining();

    timerId = setInterval(() => {
      if (!stopped) {
        setRemainingMs((prev) => (prev !== null ? Math.max(0, prev - 1000) : null));
      }
    }, 1000);

    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, []);

  // 남은 시간 포맷터 (MM:SS)
  const format = useCallback((ms: number | null): string => {
    if (ms === null) return "--:--";
    const totalSec = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSec / 60);
    const seconds = totalSec % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }, []);

  // 세션 연장: API 호출 후 상태 업데이트
  const extend = useCallback(async (): Promise<void> => {
    try {
      const res = await axiosInstance.post<RemainingResponse>("/session/extend");
      if (res.data && typeof res.data.remainingMillis === "number") {
        setRemainingMs(res.data.remainingMillis);
      }
    } catch (error) {
      console.error("Failed to extend session:", error);
    }
  }, []);

  return { remainingMs, format, extend };
}
